import { memo, useCallback, useMemo, useState, type ReactNode } from 'react';

import { cloneDeep } from 'lodash-es';
import { useFavoriteSubAccounts, useMixpanel } from '../../contexts';
import { useCallbackRef, useDynamicCallback } from '../../hooks';
import { MixpanelEvent } from '../../tokens';
import {
  AllocationValueTypeEnum,
  EMPTY_SUB_ACCOUNT_SELECTION,
  EMPTY_SUBACCOUNT_ALLOCATION,
  type Allocation,
  type SubAccount,
} from '../../types';
import { EMPTY_OBJECT, sortSubAccountsByDisplayName, toBigWithDefault } from '../../utils';
import { Button, ButtonGroup, ButtonVariants, IconButton, ToggleButton } from '../Button';
import { Box, Grid, HStack, VStack } from '../Core';
import {
  FormControlSizes,
  FormMessage,
  FuseAutocompleteResult,
  Label,
  NumberInput,
  SearchSelect,
  type RenderResultFunc,
  type SearchSelectProps,
} from '../Form';
import { Icon, IconName } from '../Icons';
import { Tooltip } from '../Tooltip';

// The contents of these change objects can be improved to contain more information as it becomes necessary.
export type AllocationChange =
  | {
      type: 'modification';
      index: number;
      key: keyof Allocation;
      value: Allocation[keyof Allocation];
    }
  | { type: 'deletion'; index: number }
  | { type: 'addition' };

export interface AllocationsSelectorProps {
  subAccountAllocations: Allocation[];
  allocationValueType?: AllocationValueTypeEnum;

  subAccountOptions: string[];
  subAccountsByName: Map<string, SubAccount>;

  disabled?: boolean;

  /**
   * The onChange handler emits two objects. The first represents the new complete state which is equivalent to a basic
   * onChange output where you just set that as your new state in your implementation.
   *
   * The second object is the delta change, so represents what the user actually did exactly. This can be referenced
   * to perform other side-effects, such as updating the default (last used) sub account in the OMSSubAccounts for example.
   */
  onAllocationsChange: (newAllocations: Allocation[], change: AllocationChange) => void;

  onAllocationsValueTypeChange: (newAllocationValueType: AllocationValueTypeEnum) => void;

  touched: { subAccountAllocations?: boolean };
  errors: { subAccountAllocations?: string };

  /** If using allocations, what currency to denominate quantity allocations in. Defaults to just "Qty". */
  quantityCurrency?: string;

  /** Whether or not to use allocations: allow the user to select more than one sub account */
  useAllocations: boolean;

  /** Set to false if you want to hide the +Allocate button. Defaults to true, showing the button. */
  showAddAllocationsButton?: boolean;

  /** Placeholder text to show in each of the search select boxes */
  placeholder?: string;

  /** A string prefix to show before the main label of `Sub Account(s)` */
  titlePrefix?: string;

  /** If provided, will show a tooltip on the label (underline-help-thing) with this content */
  helpTooltip?: ReactNode;
  /**
   * If set, will not show the toggles for switching between Percentage and Quantity AllocationValueTypes.
   * Does not modify or gate the model / values at all, so that responsibility is still on the implementer.
   *
   * Defaults to false.
   */
  hideTypeToggleButtons?: boolean;

  /**
   * Called whenever one of the sub account selectors is opened or closed. Called with the 0-based index, since there can be several
   * allocations at once. Using this callback, you can keep track of which allocation index the user is interacting with.
   */
  onSelectorOpenChange?: (index: number, isOpen: boolean) => void;

  /** Render a suffix in the dropdowns. */
  dropdownSuffix?: JSX.Element;

  /** If true, will always show the user the number input to the right of the sub account selector. Defaults to false. */
  allowEditingSingleAllocationValue?: boolean;

  /** If true, will show the star icon to toggle favorites. Defaults to false. */
  showFavorites?: boolean;

  /** When true, adds a * after the label to indicate the field is required */
  required?: boolean;

  /**
   * When true, skips sorting sub account options by DisplayName.
   * Set to true when the caller provides pre-sorted options (e.g. favorites-first in OMSSubAccounts).
   */
  suppressSubAccountSorting?: boolean;
}

const BUTTON_GROUP_WIDTH = '5rem';

const SEARCH_SELECT_STYLE: React.CSSProperties = { flex: 1 };

export const AllocationsSelector = memo(function AllocationsSelector({
  subAccountAllocations,
  allocationValueType,
  subAccountOptions: _options,
  disabled = false,
  onAllocationsChange,
  onAllocationsValueTypeChange,
  touched = EMPTY_OBJECT,
  errors = EMPTY_OBJECT,
  quantityCurrency = 'Qty',
  placeholder = '',
  titlePrefix = '',
  helpTooltip,
  useAllocations,
  hideTypeToggleButtons = false,
  showAddAllocationsButton = true,
  allowEditingSingleAllocationValue = false,
  showFavorites = false,
  required = false,
  suppressSubAccountSorting = false,
  subAccountsByName,
  dropdownSuffix,
  onSelectorOpenChange,
}: AllocationsSelectorProps) {
  const mixpanel = useMixpanel();

  const { favoriteSubAccounts, setFavoriteSubAccounts } = useFavoriteSubAccounts();
  const favoriteSubAccountsSet = useMemo(() => new Set(favoriteSubAccounts), [favoriteSubAccounts]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  const subAccountOptions = useMemo(() => {
    const sorted = suppressSubAccountSorting
      ? _options
      : sortSubAccountsByDisplayName(
          _options.map(name => ({
            Name: name,
            DisplayName: subAccountsByName?.get(name)?.DisplayName ?? name,
          }))
        ).map(s => s.Name);
    return !showFavorites
      ? sorted
      : sorted.filter(option => (showOnlyFavorites ? favoriteSubAccountsSet.has(option) : true));
  }, [
    _options,
    favoriteSubAccountsSet,
    showOnlyFavorites,
    showFavorites,
    subAccountsByName,
    suppressSubAccountSorting,
  ]);

  const handleAddAllocation = useCallback(() => {
    onAllocationsChange([...subAccountAllocations, EMPTY_SUBACCOUNT_ALLOCATION], {
      type: 'addition',
    });
  }, [onAllocationsChange, subAccountAllocations]);

  const handleUpdateAllocation = useCallback(
    <K extends keyof Allocation>(index: number, key: K, value: Allocation[K]) => {
      const newAllocations = cloneDeep(subAccountAllocations);
      newAllocations[index][key] = value;
      mixpanel.track(useAllocations ? MixpanelEvent.ChangeAllocations : MixpanelEvent.ChangeSubAccount);
      onAllocationsChange(newAllocations, { type: 'modification', index, key, value });
    },
    [onAllocationsChange, subAccountAllocations, useAllocations, mixpanel]
  );

  const handleDeleteAllocation = useCallback(
    (index: number) => {
      const newAllocations = cloneDeep(subAccountAllocations);
      newAllocations.splice(index, 1);
      mixpanel.track(useAllocations ? MixpanelEvent.ChangeAllocations : MixpanelEvent.ChangeSubAccount);

      // If after deletion we're just left with one allocation, and we don't want to show the number input anymore,
      // then we have to set the value of the now lone allocation to be "100" and Percent
      if (newAllocations.length === 1 && !allowEditingSingleAllocationValue) {
        newAllocations[0].value = '100';
        onAllocationsChange(newAllocations, { type: 'deletion', index });
        onAllocationsValueTypeChange(AllocationValueTypeEnum.Percentage);
      } else {
        onAllocationsChange(newAllocations, { type: 'deletion', index });
      }
    },
    [
      onAllocationsChange,
      subAccountAllocations,
      useAllocations,
      mixpanel,
      onAllocationsValueTypeChange,
      allowEditingSingleAllocationValue,
    ]
  );

  const handleToggleFavorite = useCallback(
    (subAccount: string) => {
      const newFavorites = favoriteSubAccountsSet;
      if (newFavorites.has(subAccount)) {
        newFavorites.delete(subAccount);
      } else {
        mixpanel.track(MixpanelEvent.FavoriteSubAccount);
        newFavorites.add(subAccount);
      }
      setFavoriteSubAccounts([...newFavorites.values()]);
    },
    [favoriteSubAccountsSet, mixpanel, setFavoriteSubAccounts]
  );

  // stable callback so searchselect doesn't have to recalc all items on every update if subAccountsByName
  const getSubAccountLabel = useDynamicCallback((subAccountName?: string) => {
    if (!subAccountName) {
      return '';
    }

    return subAccountsByName?.get(subAccountName)?.DisplayName ?? subAccountName;
  });

  const showNumberInputs = subAccountAllocations.length > 1 || allowEditingSingleAllocationValue;
  const formControlSize = subAccountAllocations.length === 1 ? FormControlSizes.Default : FormControlSizes.Small;

  const searchPrefix = useMemo(
    () => (
      <Box ml="-spacing4" data-testid="toggle-favorites-box">
        <IconButton
          ghost
          onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
          icon={showOnlyFavorites ? IconName.StarSolid : IconName.Star}
          color={showOnlyFavorites ? 'yellow.lighten' : 'gray.080'}
          className="toggle-favorites"
          data-testid="toggle-favorites"
        />
      </Box>
    ),
    [showOnlyFavorites]
  );

  return (
    <>
      <VStack alignItems="initial">
        <VStack alignItems="initial" gap="spacing4" mb="spacing16">
          <HStack justifyContent="space-between">
            <Label>
              <Tooltip tooltip={helpTooltip}>
                <div>
                  {titlePrefix}
                  {useAllocations ? 'Sub Account(s)' : 'Sub Account'}
                  {required && '*'}{' '}
                </div>
              </Tooltip>
            </Label>
            <HStack gap="spacing4">
              {useAllocations && subAccountAllocations?.length !== 1 && !disabled && !hideTypeToggleButtons && (
                <ButtonGroup style={{ width: BUTTON_GROUP_WIDTH }} size={FormControlSizes.Tiny}>
                  <ToggleButton
                    onClick={() => onAllocationsValueTypeChange(AllocationValueTypeEnum.Percentage)}
                    selected={allocationValueType === AllocationValueTypeEnum.Percentage}
                    variant={ButtonVariants.Default}
                    selectedVariant={ButtonVariants.Priority}
                    size={FormControlSizes.Tiny}
                    data-testid="oms-subaccounts-percentage-button"
                  >
                    %
                  </ToggleButton>
                  <ToggleButton
                    onClick={() => onAllocationsValueTypeChange(AllocationValueTypeEnum.Quantity)}
                    selected={allocationValueType === AllocationValueTypeEnum.Quantity}
                    variant={ButtonVariants.Default}
                    selectedVariant={ButtonVariants.Priority}
                    size={FormControlSizes.Tiny}
                    data-testid="oms-subaccounts-quantity-button"
                  >
                    {quantityCurrency}
                  </ToggleButton>
                </ButtonGroup>
              )}
              {useAllocations && showAddAllocationsButton && (
                <Button
                  endIcon={IconName.Plus}
                  size={FormControlSizes.Tiny}
                  onClick={handleAddAllocation}
                  data-testid="add-allocation-button"
                  disabled={disabled}
                >
                  Allocate
                </Button>
              )}
            </HStack>
          </HStack>
          {subAccountAllocations?.map((allocation, index) => (
            <Grid
              key={index}
              data-testid={`allocation-row-${index}`}
              gap="spacing2"
              columns={showNumberInputs ? '2fr 1fr auto' : '1fr'}
            >
              {/* When adding new props to this component, if they would cause the search select to rerender each oms render, please memoize them in the wrapper. See jsdoc on the wrapper. */}
              <MemoizedAllocationsSearchSelect
                selectorIndex={index}
                data-testid={`sub-account-select-${index}`}
                placeholder={placeholder}
                options={subAccountOptions}
                selection={allocation.subAccount}
                showClear={allocation.subAccount !== EMPTY_SUB_ACCOUNT_SELECTION}
                disabled={disabled}
                onChange={subAccount =>
                  handleUpdateAllocation(index, 'subAccount', subAccount || EMPTY_SUB_ACCOUNT_SELECTION)
                }
                onClearClick={() => handleUpdateAllocation(index, 'subAccount', EMPTY_SUB_ACCOUNT_SELECTION)}
                initialSortByLabel={false}
                getLabel={getSubAccountLabel}
                style={SEARCH_SELECT_STYLE}
                touched={!!touched.subAccountAllocations}
                size={formControlSize}
                invalid={
                  (allocation.subAccount == null || allocation.subAccount === EMPTY_SUB_ACCOUNT_SELECTION) &&
                  !!errors.subAccountAllocations &&
                  !!touched.subAccountAllocations
                }
                simpleSortFilter
                onIsOpenChange={changes => onSelectorOpenChange?.(index, changes.isOpen)}
                dropdownSuffix={dropdownSuffix}
                searchPrefix={showFavorites && searchPrefix}
                onToggleFavorite={handleToggleFavorite}
                showFavorites={showFavorites}
              />
              {showNumberInputs && (
                <>
                  <NumberInput
                    invalid={
                      (allocation.value == null ||
                        allocation.value === '' ||
                        toBigWithDefault(allocation.value, 0).lte(0)) &&
                      !!errors.subAccountAllocations &&
                      !!touched.subAccountAllocations
                    }
                    data-testid={`allocation-value-input-${index}`}
                    disabled={disabled}
                    touched={!!touched.subAccountAllocations}
                    autoComplete="off"
                    onChange={value => handleUpdateAllocation(index, 'value', value)}
                    value={allocation.value?.toString() || ''}
                    size={formControlSize}
                    suffix={
                      allocationValueType === AllocationValueTypeEnum.Percentage
                        ? '%'
                        : allocationValueType === AllocationValueTypeEnum.Quantity
                          ? quantityCurrency
                          : ''
                    }
                  />
                  <IconButton
                    disabled={disabled || subAccountAllocations.length === 1}
                    size={formControlSize}
                    icon={IconName.Trash}
                    onClick={() => handleDeleteAllocation(index)}
                    data-testid="allocation-delete-button"
                  />
                </>
              )}
            </Grid>
          ))}
          {touched.subAccountAllocations && errors.subAccountAllocations && (
            <FormMessage data-testid="oms-subaccounts-error" textAlign="left">
              {errors.subAccountAllocations}
            </FormMessage>
          )}
        </VStack>
      </VStack>
    </>
  );
});

interface MemoizedAllocationsSearchSelectProps extends SearchSelectProps<string> {
  selectorIndex: number;
  onToggleFavorite: (subAccount: string) => void;
  showFavorites?: boolean;
}

/**
 * Our SearchSelect component doesn't play nice with unstable props. It'll keep re-rendering and making the items
 * in the dropdown result list unresponsive. To get around this, we make specific props here stable.
 */
const MemoizedAllocationsSearchSelect = ({
  selectorIndex,
  onChange,
  onClearClick,
  onIsOpenChange,
  onToggleFavorite,
  showFavorites,
  ...props
}: MemoizedAllocationsSearchSelectProps) => {
  const stableOnChange = useDynamicCallback(onChange);
  const stableOnClearClick = useDynamicCallback(onClearClick);
  const stableOnIsOpenChange = useDynamicCallback(onIsOpenChange);
  const { favoriteSubAccounts } = useFavoriteSubAccounts();

  const renderFavoriteIcon = useCallback(
    (subAccount: string) => {
      if (!showFavorites) {
        return null;
      }
      const isFavorite = favoriteSubAccounts?.includes(subAccount);
      return (
        <Icon
          icon={isFavorite ? IconName.StarSolid : IconName.Star}
          color={isFavorite ? 'yellow.lighten' : 'gray.080'}
          size="fontSizeLarge"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onToggleFavorite(subAccount);
          }}
          p="spacing12"
          spaceBefore="-spacing12"
        />
      );
    },
    [favoriteSubAccounts, showFavorites, onToggleFavorite]
  );

  const renderResult: RenderResultFunc<string> = useCallbackRef((searchResults, disabled) =>
    FuseAutocompleteResult(searchResults, disabled, renderFavoriteIcon)
  );

  return (
    <SearchSelect
      data-testid={`sub-account-select-${selectorIndex}`}
      onChange={stableOnChange}
      onClearClick={stableOnClearClick}
      onIsOpenChange={stableOnIsOpenChange}
      renderResult={renderResult}
      {...props}
    />
  );
};
